import useGuardedTabs from '@boHooks/useGuardedTabs';
import ContextTab from '@boViews/shared/ContextTab';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import TabBar from '@sharedComponents/TabBar';
import Tag from '@sharedComponents/Tag';
import Typography from '@sharedComponents/Typography';
import { useTranslation } from 'react-i18next';

import InformationsTab from './InformationsTab';
import PlateformesTab from './PlateformesTab';

const INFORMATION_TAB = 0;
const PLATFORMS_TAB = 1;
const CONTEXT_TAB = 2;

const ClientDetail = ({
    client = null,
    csmUsers = [],
    platforms = [],
    platformsLoading = false,
    onBack = () => {},
    onSave = () => {},
    onCreatePlatform = () => {},
    onViewPlatform = () => {},
    context = null,
    contextLoading = false,
    contextError = null,
    onSaveContext = async () => null,
    onContextDirtyChange = () => {},
} = {}) => {
    const { t } = useTranslation('bo');
    const { activeTab, selectTab, reportDirty } = useGuardedTabs(CONTEXT_TAB, onContextDirtyChange, INFORMATION_TAB);

    if (!client) return null;

    const tabs = [
        { label: t('Information'), selected: activeTab === INFORMATION_TAB },
        { label: t('Platforms'), selected: activeTab === PLATFORMS_TAB },
        { label: t('Context'), selected: activeTab === CONTEXT_TAB },
    ];

    return (
        <FlexBox flexDirection="column" gap="m" additionnalStyles={{ padding: 'var(--space-l) var(--space-xl)' }}>
            <Button ghost noPadding icon={faArrowLeft} label={t('Back to clients')} action={onBack} style={{ alignSelf: 'flex-start' }} />

            <FlexBox justifyContent="space-between" alignItems="center">
                <FlexBox flexDirection="column" gap="xxs">
                    <Typography fontSize="xl" fontWeight="bold" color="branded-primary" seo={1}>
                        {client.name}
                    </Typography>
                    {client.reference && <Typography color="neutral-secondary">{client.reference}</Typography>}
                </FlexBox>
                <Tag color={client.active ? 'success' : 'disabled'}>{client.active ? t('Active') : t('Inactive')}</Tag>
            </FlexBox>

            <FlexBox additionnalStyles={{ borderBottom: '1px solid var(--background-color-neutral-secondary)' }}>
                <TabBar tabs={tabs} onClick={selectTab} />
            </FlexBox>

            {/* Remounted after each save so the form restarts from the server-normalized values. */}
            {activeTab === INFORMATION_TAB && (
                <InformationsTab key={client.updatedAt ?? client.uuid} client={client} csmUsers={csmUsers} onSave={onSave} />
            )}
            {activeTab === PLATFORMS_TAB && (
                <PlateformesTab
                    platforms={platforms}
                    loading={platformsLoading}
                    canAddPlatform={client.active}
                    onAddPlatform={onCreatePlatform}
                    onViewPlatform={onViewPlatform}
                />
            )}
            {activeTab === CONTEXT_TAB && (
                <ContextTab
                    context={context}
                    loading={contextLoading}
                    error={contextError}
                    emptyTitle={t('No context for this client')}
                    emptyDescription={t(
                        'Note what the team should know about this client: contacts, uses, expectations, points of attention.',
                    )}
                    onSave={onSaveContext}
                    onDirtyChange={reportDirty}
                />
            )}
        </FlexBox>
    );
};

export default ClientDetail;
